import CheckIcon from "@mixitone/icons/check.svg";
import type { ApplicationController, ControllerState } from "@mixitone/mvc";
import { isNil, omit } from "@mixitone/util";
import clsx from "clsx";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

type Props = Omit<React.InputHTMLAttributes<HTMLInputElement>, "prefix"> & {
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  unstyled?: boolean;
  suffix?: string;
  prefix?: React.ReactNode;
  ref?: React.Ref<HTMLInputElement>;
  showValid?: boolean;
  containerClassName?: string;
};

export const Input: React.FC<Props> & { style: string } = ({ ref, ...props }) => {
  const [value, setValue] = useState(props.value);
  const rest = omit(props, ["value", "onChange", "suffix", "prefix", "showValid"]);
  const { suffix, prefix, error, showValid } = props;
  const inputRef = useRef<HTMLInputElement>(undefined);

  useEffect(() => {
    // While focused, local state is authoritative — avoid syncing props and causing an extra render.
    if (inputRef.current === document.activeElement) return;
    if (props.value !== value) {
      setValue(props.value);
    }
  }, [props.value, value]);

  // @ts-ignore dev mode only
  if (import.meta.env.MODE === "development") {
    useEffect(() => {
      const abortController = new AbortController();

      if (inputRef.current) {
        inputRef.current.addEventListener(
          "change",
          (e) => {
            if (!e.target) return;
            const target = e.target as HTMLInputElement;
            if (target.value === value) return;
            // @ts-ignore dev mode only
            handleChange(e);
          },
          { signal: abortController.signal },
        );
      }

      return () => {
        abortController.abort();
      };
    }, [inputRef.current]);
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    props.onChange(e);
  };

  const className = props.unstyled
    ? ""
    : twMerge(
        Input.style,
        clsx({
          "px-0 pl-5 pr-2": prefix,
        }),
      );

  const combinedRef = useCallback(
    (element: HTMLInputElement) => {
      inputRef.current = element;
      if (typeof ref === "function") {
        ref(element);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLInputElement>).current = element;
      }
    },
    [ref],
  );

  return (
    <>
      <div className={clsx("relative flex items-center", props.containerClassName)}>
        {prefix && <div className="absolute mt-1 text-sm text-gray-500 left-2">{prefix}</div>}
        <input
          value={value ?? ""}
          onChange={handleChange}
          {...omit(rest, ["unstyled"])}
          className={twMerge("peer", className, rest.className)}
          ref={combinedRef}
        />
        {showValid && (
          <div className={clsx("absolute right-2 mt-1 peer-invalid:hidden", {})}>
            <div className="flex items-center justify-center w-5 h-5 bg-green-500 border border-green-700 rounded-full">
              <CheckIcon className="w-2 h-2 text-white" />
            </div>
          </div>
        )}
        {suffix && <div className="absolute mt-1 text-sm text-gray-500 right-8">{suffix}</div>}
      </div>
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </>
  );
};

// biome-ignore lint/suspicious/noExplicitAny: meh
export function bindController<C extends ApplicationController<any, any, any>>(controller: C) {
  return function <F extends keyof ControllerState<C>>(
    field: F,
    converter: (value: string) => ControllerState<C>[F] = (value: string) => value,
  ) {
    const value = controller.state[field];
    const error = controller.state.errors?.[field];

    return {
      name: field,
      id: field,
      value: String(isNil(value) ? "" : value) as string,
      // biome-ignore lint/suspicious/noExplicitAny: meh
      onChange: (e: React.ChangeEvent<any>) => {
        if (typeof controller.state.errors === "object") {
          controller.setState({
            errors: {
              ...controller.state.errors,
              [field]: undefined,
            },
          });
        }
        controller.setState({
          [field]: converter(e.target.value),
        });
      },
      error,
    };
  };
}

export function bindControllerState<
  // biome-ignore lint/suspicious/noExplicitAny: meh
  C extends ApplicationController<any, any, any>,
  K extends keyof ControllerState<C>,
>(controller: C, key: K) {
  const unsetErrors = function <F extends keyof ControllerState<C>[K]>(field: F) {
    if (typeof controller.state.errors === "object") {
      controller.setState({
        errors: {
          ...controller.state.errors,
          [field]: undefined,
        },
      });
    }
  };

  const bind = function <F extends keyof ControllerState<C>[K], T extends ControllerState<C>[K][F]>(
    field: F,
    converter: (value: string) => ControllerState<C>[K][F] = (value: string) => value,
  ) {
    const value = controller.state[key][field] as T;
    const error = controller.state.errors?.[field];

    return {
      name: field,
      id: field,
      value,
      // biome-ignore lint/suspicious/noExplicitAny: meh
      onChange: (e: React.ChangeEvent<any>) => {
        unsetErrors(field);
        controller.state[key][field] = converter(e.target.value);
      },
      error,
    };
  };

  const bindDatetime = function <F extends keyof ControllerState<C>[K], T extends ControllerState<C>[K][F]>(
    field: F,
  ) {
    const value = dayjs(controller.state[key][field] as T).format("YYYY-MM-DDTHH:mm");
    const error = controller.state.errors?.[field];

    return {
      name: field,
      id: field,
      value,
      // biome-ignore lint/suspicious/noExplicitAny: meh
      onChange: (e: React.ChangeEvent<any>) => {
        unsetErrors(field);
        controller.state[key][field] = dayjs(e.target.value).toDate();
      },
      error,
    };
  };

  const bindChecked = function <F extends keyof ControllerState<C>[K], T extends ControllerState<C>[K][F]>(
    field: F,
    converter: (value: boolean) => ControllerState<C>[K][F] = (value: boolean) => value,
  ) {
    const value = Boolean(controller.state[key][field] as T);
    const error = controller.state.errors?.[field];

    return {
      name: field,
      id: field,
      checked: value,
      // biome-ignore lint/suspicious/noExplicitAny: meh
      onChange: (e: React.ChangeEvent<any>) => {
        unsetErrors(field);
        controller.state[key][field] = converter(e.target.checked);
      },
      error,
    };
  };

  return { bind, bindChecked, bindDatetime };
}

Input.style =
  "mt-1 block w-full rounded border border-gray-300 bg-white py-2 px-2 disabled:bg-gray-100 disabled:cursor-not-allowed";
